import cloneDeep from 'lodash/cloneDeep';
import { checkIsTransparentColor } from '@/utils/color-kit';
import { glKit } from '@/utils/gl-kit';
import { getMainLayerRenderSize } from '@/utils/layer-kit';
import { rotatePoint } from '@/utils/math-kit';
import type { IBackgroundLayerProps, IImageLayerProps, ILayerProps, IMainLayerScope, IPoint } from '@/version-types';

/**
 * 把异步生成的主图 URL 合入最新正式图层副本
 *
 * 只认领图片 URL 和 GL 运行标记，避免调色异步完成后覆盖最新的 mask、几何或其他协同字段。
 *
 * @param latestLayer - 提交时重新读取的最新主图
 * @param url - 异步 GL 生成的图片 URL
 * @returns 保留最新协同字段并替换图片 URL 的主图副本
 */
export function applyRenderedMainLayerURL(latestLayer: ILayerProps, url: string) {
    const committedLayer = cloneDeep(latestLayer);
    (committedLayer.data as IImageLayerProps).url = url;
    glKit.clearGL(committedLayer);
    return committedLayer;
}

/**
 * 将鼠标坐标点转化为主图的坐标点
 * @param mainLayer - 主图层
 * @param point - 已转换到场景坐标系的鼠标点
 * @returns 去除主图位移、旋转和翻转后的完整图片渲染坐标
 */
export function convertMousePointToLayerPoint(mainLayer: ILayerProps, point: IPoint) {
    const { left, top, rotate, data } = mainLayer;
    const { width, height } = getMainLayerRenderSize(mainLayer);
    const { flipH, flipV } = (data as IImageLayerProps).imageTransform;

    // 旋转的时候需要先转换为笛卡尔坐标系，DOM 里面的坐标系 y 是向下的
    const newPoint = rotatePoint(
        {
            x: point.x - left - width / 2,
            y: -(point.y - top - height / 2),
        },
        rotate,
    );

    // 再转换为 DOM 的坐标系
    newPoint.y = -newPoint.y;

    if (flipV) {
        newPoint.y = -newPoint.y;
    }

    if (flipH) {
        newPoint.x = -newPoint.x;
    }

    return { x: newPoint.x + width / 2, y: newPoint.y + height / 2 };
}

/**
 * 检查当前主图操作是否是否需要合并所有图层
 * @param scope 当前操作作用范围
 * @param layers 画布中的所有图层
 * @returns 是否需要合并图层
 */
export function checkNeedMergeCanvas(scope: IMainLayerScope | undefined, layers: ILayerProps[]) {
    if (scope !== 'canvas') return false;

    // TODO: @cxz1 检查图片是否带有透明度
    // 如果有背景，则需要合并为一张图片进行处理
    const backgroundLayerData = layers.find((layer) => layer.layerType === 'bg')?.data as
        | IBackgroundLayerProps
        | undefined;
    const hasBackground =
        (backgroundLayerData?.backgroundColor && !checkIsTransparentColor(backgroundLayerData.backgroundColor)) ||
        backgroundLayerData?.backgroundGradient ||
        backgroundLayerData?.url;

    if (hasBackground) return true;

    // 如果有其它图层，则需要合并为一张图片进行处理
    const extraLayers = layers.filter((layer) => ['bg', 'mainImg'].indexOf(layer.layerType) === -1);
    const hasExtraLayer = extraLayers.length > 0;

    return hasExtraLayer;
}
